"use client";

import { usePathname } from "next/navigation";

import { ARTICLES } from "@/app/(marketing)/(content)/_content";
import { breadcrumbSchema, JsonLd } from "@/app/lib/seo/jsonLd";

/**
 * Emits BreadcrumbList structured data for the current marketing sub-page,
 * derived from the URL. Rendered once in the marketing layout so every sub-page
 * gets a breadcrumb without per-page wiring. It server-renders into the initial
 * HTML, so crawlers read it without executing JavaScript.
 */

/** Human-readable names for path segments. Unknown segments fall back to a
 *  title-cased slug. */
const SEGMENT_LABELS: Record<string, string> = {
  platform: "Platform",
  services: "Services",
  about: "About",
  integrations: "Integrations",
  slack: "Slack",
  zoom: "Zoom",
  notion: "Notion",
  zapier: "Zapier",
  "public-api": "Public API",
  pricing: "Pricing",
  "use-cases": "Use Cases",
  vision: "Vision",
  support: "Support",
  privacy: "Privacy",
  terms: "Terms",
  subprocessors: "Subprocessors",
  "decision-making-frameworks": "Decision-Making Frameworks",
  "consequential-thinking-framework": "Consequential Thinking Framework",
  "decision-difficulty-framework": "Decision Difficulty Framework",
  resources: "Resources",
  compare: "Compare",
};

/** Article slugs resolve to their real titles, so a content URL's last
 *  breadcrumb reads as the article rather than a title-cased slug. */
const ARTICLE_TITLES: Record<string, string> = Object.fromEntries(
  ARTICLES.map((article) => [article.slug, article.title]),
);

/** Sub-pages kept out of search results get no breadcrumb markup. */
const NOINDEX_SEGMENTS = new Set(["dpa", "security"]);

function labelFor(segment: string): string {
  return (
    SEGMENT_LABELS[segment] ??
    ARTICLE_TITLES[segment] ??
    segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export default function BreadcrumbJsonLd() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);

  // The homepage needs no breadcrumb, and noindex pages are excluded.
  if (segments.length === 0 || NOINDEX_SEGMENTS.has(segments[0])) {
    return null;
  }

  const trail = [{ name: "Home", path: "/" }];
  let cumulative = "";

  for (const segment of segments) {
    cumulative += `/${segment}`;
    trail.push({ name: labelFor(segment), path: cumulative });
  }

  return <JsonLd data={breadcrumbSchema(trail)} />;
}
